import React from "react";
import { ArrowDownIcon, ArrowUpIcon, ChevronDownIcon, PlusIcon, Trash2Icon } from "lucide-react";

import { InvitationFileInput } from "@javascript/components/InvitationFileInput";
import { RichTextEditor } from "@javascript/components/RichTextEditor";
import { TimezonePicker, type TimezoneOption } from "@javascript/components/TimezonePicker";
import { VenueMapField } from "@javascript/components/VenueMapField";
import { Button } from "@javascript/components/ui/button";
import { Field, FieldDescription, FieldError, FieldLabel, FieldLegend, FieldSet } from "@javascript/components/ui/field";
import { Input } from "@javascript/components/ui/input";
import { Textarea } from "@javascript/components/ui/textarea";

export type ContentPath = Array<string | number>;

export type ContentFieldSchema = {
  kind: "field";
  key: string;
  label: string;
  description: string | null;
  input_kind: "text_field" | "text_area" | "rich_text_area" | "date_field" | "datetime_field" | "timezone_picker" | "url_field" | "select" | "image_upload";
  required: boolean;
  options: Array<{ value: string; label: string }>;
  default: string | null;
  placeholder: string | null;
  allow_other?: boolean;
};

export type ContentObjectSchema = {
  kind: "object";
  key: string;
  label: string;
  description: string | null;
  editor_kind?: "venue" | null;
  children: ContentNodeSchema[];
};

export type ContentCollectionSchema = {
  kind: "collection";
  key: string;
  label: string;
  description: string | null;
  item_label: string;
  add_label: string;
  min_items: number;
  max_items: number | null;
  item: ContentObjectSchema;
};

export type ContentNodeSchema = ContentFieldSchema | ContentObjectSchema | ContentCollectionSchema;

export type ContentValidationError = { path: ContentPath; message: string };
export type ContentAsset = { url: string; filename: string };
export type VenueMapSearch = { available: boolean; apiKey: string | null; mapId: string | null };
type EnabledVenueMapSearch = VenueMapSearch & { apiKey: string };
type CoordinateDescriptor = { name: string; value: string | null; errors: string[] };

type ContentValue = Record<string, unknown>;

export type InvitationContentEditorProps = {
  sections: ContentNodeSchema[];
  content: ContentValue;
  assets: Record<string, ContentAsset>;
  errors: ContentValidationError[];
  timezones?: TimezoneOption[];
  unsetTimezonePaths?: ContentPath[];
  venueMapSearch?: VenueMapSearch;
  topLevelHeading?: "visible" | "sr-only";
};

export function InvitationContentEditor({
  sections,
  content,
  assets,
  errors,
  timezones = [],
  unsetTimezonePaths = [],
  venueMapSearch,
  topLevelHeading = "visible",
}: InvitationContentEditorProps) {
  return (
    <div className="flex flex-col gap-12">
      {sections.map((section) => (
        <ContentNode
          key={section.key}
          schema={section}
          value={content[section.key]}
          path={[section.key]}
          assets={assets}
          errors={errors}
          timezones={timezones}
          unsetTimezonePaths={unsetTimezonePaths}
          venueMapSearch={venueMapSearch}
          legendClassName={topLevelHeading === "sr-only" ? "sr-only" : undefined}
        />
      ))}
    </div>
  );
}

function ContentNode({
  schema,
  value,
  path,
  assets,
  errors,
  timezones,
  unsetTimezonePaths,
  venueMapSearch,
  legendClassName,
}: {
  schema: ContentNodeSchema;
  value: unknown;
  path: ContentPath;
  assets: Record<string, ContentAsset>;
  errors: ContentValidationError[];
  timezones: TimezoneOption[];
  unsetTimezonePaths: ContentPath[];
  venueMapSearch?: VenueMapSearch;
  legendClassName?: string;
}) {
  if (schema.kind === "field") {
    return <ContentField schema={schema} value={value} path={path} assets={assets} errors={errors} timezones={timezones} unsetTimezonePaths={unsetTimezonePaths} />;
  }

  if (schema.kind === "collection") {
    return (
      <ContentCollection
        schema={schema}
        value={value}
        path={path}
        assets={assets}
        errors={errors}
        timezones={timezones}
        unsetTimezonePaths={unsetTimezonePaths}
        venueMapSearch={venueMapSearch}
        legendClassName={legendClassName}
      />
    );
  }

  const objectValue = isObject(value) ? value : {};
  const enabledVenueMapSearch: EnabledVenueMapSearch | null = (
    schema.editor_kind === "venue" && venueMapSearch?.available && venueMapSearch.apiKey
  ) ? { ...venueMapSearch, apiKey: venueMapSearch.apiKey } : null;
  const visibleChildren = enabledVenueMapSearch ? schema.children.filter((child) => !isCoordinateField(child)) : schema.children;

  return (
    <FieldSet>
      <FieldLegend className={legendClassName}>{schema.label}</FieldLegend>
      {schema.description && <FieldDescription>{schema.description}</FieldDescription>}
      {visibleChildren.map((child) => (
        <ContentNode
          key={child.key}
          schema={child}
          value={objectValue[child.key]}
          path={[...path, child.key]}
          assets={assets}
          errors={errors}
          timezones={timezones}
          unsetTimezonePaths={unsetTimezonePaths}
          venueMapSearch={venueMapSearch}
        />
      ))}
      {enabledVenueMapSearch && (
        <VenueCoordinates
          schema={schema}
          value={objectValue}
          path={path}
          errors={errors}
          venueMapSearch={enabledVenueMapSearch}
        />
      )}
    </FieldSet>
  );
}

function ContentCollection({
  schema,
  value,
  path,
  assets,
  errors,
  venueMapSearch,
  timezones,
  unsetTimezonePaths,
  legendClassName,
}: {
  schema: ContentCollectionSchema;
  value: unknown;
  path: ContentPath;
  assets: Record<string, ContentAsset>;
  errors: ContentValidationError[];
  venueMapSearch?: VenueMapSearch;
  timezones: TimezoneOption[];
  unsetTimezonePaths: ContentPath[];
  legendClassName?: string;
}) {
  const [items, setItems] = React.useState<unknown[]>(() => (Array.isArray(value) ? value : []));
  const [deviceTimezoneItemIds, setDeviceTimezoneItemIds] = React.useState<Set<string>>(() => new Set());
  const mayAdd = schema.max_items === null || items.length < schema.max_items;
  const mayRemove = items.length > schema.min_items;
  const newTimezonePaths = items.flatMap((itemValue, index) => {
    const item = isObject(itemValue) ? itemValue : {};
    return deviceTimezoneItemIds.has(String(item.id)) ? [ [...path, index, "timezone"] ] : [];
  });
  const timezonePaths = [ ...unsetTimezonePaths, ...newTimezonePaths ];

  function addItem() {
    const item = newCollectionItem(schema.item);
    if (schema.key === "events") setDeviceTimezoneItemIds((ids) => new Set(ids).add(String(item.id)));

    setItems((currentItems) => [ ...currentItems, item ]);
  }

  return (
    <FieldSet>
      <FieldLegend className={legendClassName}>{schema.label}</FieldLegend>
      {schema.description && <FieldDescription>{schema.description}</FieldDescription>}
      {items.map((itemValue, index) => {
        const item = isObject(itemValue) ? itemValue : {};
        const itemPath = [...path, index];
        const itemId = String(item.id ?? index);

        return (
          <div key={itemId} className="space-y-5 border-l border-border pl-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium">{schema.item_label} {index + 1}</p>
              <div className="flex flex-wrap justify-end gap-1">
                {index > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="min-h-11"
                    onClick={() => setItems((currentItems) => moveItem(currentItems, index, index - 1))}
                  >
                    <ArrowUpIcon aria-hidden="true" />
                    Move {schema.item_label} {index + 1} up
                  </Button>
                )}
                {index < items.length - 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="min-h-11"
                    onClick={() => setItems((currentItems) => moveItem(currentItems, index, index + 1))}
                  >
                    <ArrowDownIcon aria-hidden="true" />
                    Move {schema.item_label} {index + 1} down
                  </Button>
                )}
                {mayRemove && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="min-h-11"
                    onClick={() => setItems((currentItems) => currentItems.filter((_item, itemIndex) => itemIndex !== index))}
                  >
                    <Trash2Icon aria-hidden="true" />
                    Remove {schema.item_label} {index + 1}
                  </Button>
                )}
              </div>
            </div>
            <input type="hidden" name={inputName([...itemPath, "id"])} value={itemId} />
            {schema.item.children.map((child) => (
              <ContentNode
                key={child.key}
                schema={child}
                value={item[child.key]}
                path={[...itemPath, child.key]}
                assets={assets}
                errors={errors}
                timezones={timezones}
                unsetTimezonePaths={timezonePaths}
                venueMapSearch={venueMapSearch}
              />
            ))}
          </div>
        );
      })}
      {mayAdd && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="min-h-11 w-fit"
          onClick={addItem}
        >
          <PlusIcon aria-hidden="true" />
          {schema.add_label}
        </Button>
      )}
    </FieldSet>
  );
}

function VenueCoordinates({
  schema,
  value,
  path,
  errors,
  venueMapSearch,
}: {
  schema: ContentObjectSchema;
  value: ContentValue;
  path: ContentPath;
  errors: ContentValidationError[];
  venueMapSearch: EnabledVenueMapSearch;
}) {
  const latitudeSchema = schema.children.find((child): child is ContentFieldSchema => child.kind === "field" && child.key === "latitude");
  const longitudeSchema = schema.children.find((child): child is ContentFieldSchema => child.kind === "field" && child.key === "longitude");

  if (!latitudeSchema || !longitudeSchema) return null;

  const latitudeField = coordinateDescriptor(latitudeSchema, value.latitude, [...path, "latitude"], errors);
  const longitudeField = coordinateDescriptor(longitudeSchema, value.longitude, [...path, "longitude"], errors);
  const coordinateErrors = [...latitudeField.errors, ...longitudeField.errors];
  const invalid = coordinateErrors.length > 0;
  const searchId = `${inputId(path)}_location_search`;

  return (
    <Field data-invalid={invalid}>
      <FieldLabel htmlFor={searchId}>Location</FieldLabel>
      <VenueMapField
        apiKey={venueMapSearch.apiKey}
        mapId={venueMapSearch.mapId ?? undefined}
        searchId={searchId}
        latitudeField={latitudeField}
        longitudeField={longitudeField}
      />
      <FieldDescription>Search for the venue, then drag the pin if you need a more precise location.</FieldDescription>
      <FieldError errors={coordinateErrors.map((message) => ({ message }))} />
    </Field>
  );
}

function coordinateDescriptor(
  schema: ContentFieldSchema,
  value: unknown,
  path: ContentPath,
  errors: ContentValidationError[],
): CoordinateDescriptor {
  const fieldErrors = errorsAtPath(errors, path);

  return {
    name: inputName(path),
    value: typeof value === "string" ? value : schema.default,
    errors: fieldErrors,
  };
}

function ContentField({
  schema,
  value,
  path,
  assets,
  errors,
  timezones,
  unsetTimezonePaths,
}: {
  schema: ContentFieldSchema;
  value: unknown;
  path: ContentPath;
  assets: Record<string, ContentAsset>;
  errors: ContentValidationError[];
  timezones: TimezoneOption[];
  unsetTimezonePaths: ContentPath[];
}) {
  const id = inputId(path);
  const name = inputName(path);
  const stringValue = typeof value === "string" ? value : schema.default ?? "";
  const fieldErrors = errorsAtPath(errors, path);
  const invalid = fieldErrors.length > 0;

  return (
    <Field data-invalid={invalid}>
      <FieldLabel htmlFor={id} className="flex items-baseline justify-between gap-3">
        <span>{schema.label}</span>
        <span aria-hidden="true" className="text-xs font-normal text-muted-foreground">
          {schema.required ? "Required" : "Optional"}
        </span>
      </FieldLabel>
      <ContentControl schema={schema} id={id} name={name} value={stringValue} invalid={invalid} path={path} assets={assets} timezones={timezones} useDeviceTimezone={pathsContain(unsetTimezonePaths, path)} />
      {schema.description && <FieldDescription>{schema.description}</FieldDescription>}
      {schema.input_kind === "datetime_field" && <FieldDescription>Use your local date and time.</FieldDescription>}
      <FieldError errors={fieldErrors.map((message) => ({ message }))} />
    </Field>
  );
}

function ContentControl({
  schema,
  id,
  name,
  value,
  invalid,
  path,
  assets,
  timezones,
  useDeviceTimezone,
}: {
  schema: ContentFieldSchema;
  id: string;
  name: string;
  value: string;
  invalid: boolean;
  path: ContentPath;
  assets: Record<string, ContentAsset>;
  timezones: TimezoneOption[];
  useDeviceTimezone: boolean;
}) {
  const commonProps = {
    id,
    name,
    defaultValue: value,
    required: schema.required,
    "aria-invalid": invalid,
    placeholder: schema.placeholder ?? undefined,
  };

  switch (schema.input_kind) {
    case "text_area":
      return <Textarea {...commonProps} className="min-h-24" />;
    case "rich_text_area":
      return (
        <RichTextEditor
          id={id}
          name={name}
          label={schema.label}
          initialHtml={value}
          invalid={invalid}
          placeholder={schema.placeholder ?? undefined}
        />
      );
    case "date_field":
      return <Input {...commonProps} type="date" className="min-h-11" />;
    case "datetime_field":
      return <Input {...commonProps} type="datetime-local" className="min-h-11" />;
    case "timezone_picker":
      return <TimezonePicker id={id} name={name} value={value} timezones={timezones} useDeviceTimezone={useDeviceTimezone} />;
    case "url_field":
      return <Input {...commonProps} type="url" className="min-h-11" />;
    case "select":
      return <ChoiceControl schema={schema} id={id} name={name} value={value} invalid={invalid} />;
    case "image_upload":
      return (
        <>
          <input type="hidden" name={name} value={value} />
          <InvitationFileInput
            id={id}
            name={nestedInputName("asset_uploads", path)}
            accept="image/*"
            url={assets[value]?.url}
            required={schema.required && value.length === 0}
            prompt={`Drop your ${schema.label.toLowerCase()} here, or browse`}
            previewAlt={`Selected ${schema.label.toLowerCase()}`}
          />
        </>
      );
    default:
      return <Input {...commonProps} className="min-h-11" />;
  }
}

const OTHER_CHOICE = "__other__";

// Exactly one control carries the field's name at a time, so the submitted
// document keeps one value per schema path and native validation still applies
// to whichever control the couple is actually using.
function ChoiceControl({
  schema,
  id,
  name,
  value,
  invalid,
}: {
  schema: ContentFieldSchema;
  id: string;
  name: string;
  value: string;
  invalid: boolean;
}) {
  const isListed = schema.options.some((option) => option.value === value);
  const startsOnOther = Boolean(schema.allow_other) && !isListed && value.length > 0;
  const [choice, setChoice] = React.useState(startsOnOther ? OTHER_CHOICE : isListed ? value : "");
  const [ownWording, setOwnWording] = React.useState(startsOnOther ? value : "");
  const choosingOther = choice === OTHER_CHOICE;

  return (
    <>
      <div data-slot="native-select-control" className="relative">
        <select
          id={id}
          name={choosingOther ? undefined : name}
          value={choice}
          onChange={(event) => setChoice(event.target.value)}
          required={schema.required}
          aria-invalid={invalid}
          data-slot="native-select"
          className="min-h-11 w-full appearance-none rounded-md border border-input bg-background py-1 pr-10 pl-3 text-base text-foreground shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 md:text-sm dark:bg-input/30"
        >
          {schema.options.map((option) => (
            <option key={option.value} value={option.value} className="bg-popover text-popover-foreground">
              {option.label}
            </option>
          ))}
          {schema.allow_other && (
            <option value={OTHER_CHOICE} className="bg-popover text-popover-foreground">
              Other…
            </option>
          )}
        </select>
        <ChevronDownIcon
          aria-hidden="true"
          data-slot="native-select-indicator"
          className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-muted-foreground"
        />
      </div>
      {choosingOther && (
        <Input
          name={name}
          value={ownWording}
          onChange={(event) => setOwnWording(event.target.value)}
          required={schema.required}
          aria-invalid={invalid}
          aria-label={`${schema.label} — your own wording`}
          data-slot="choice-other"
          placeholder={schema.placeholder ?? undefined}
          className="min-h-11"
        />
      )}
    </>
  );
}

function inputName(path: ContentPath): string {
  return nestedInputName("content", path);
}

function nestedInputName(root: string, path: ContentPath): string {
  return `${root}${path.map((segment) => `[${segment}]`).join("")}`;
}

function inputId(path: ContentPath): string {
  return `content_${path.join("_").replace(/[^a-zA-Z0-9_-]/g, "_")}`;
}

function isObject(value: unknown): value is ContentValue {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isCoordinateField(schema: ContentNodeSchema): boolean {
  return schema.kind === "field" && (schema.key === "latitude" || schema.key === "longitude");
}

function errorsAtPath(errors: ContentValidationError[], path: ContentPath): string[] {
  return errors
    .filter((error) => pathsEqual(error.path, path))
    .map((error) => error.message);
}

function pathsEqual(left: ContentPath, right: ContentPath): boolean {
  return left.length === right.length && left.every((segment, index) => String(segment) === String(right[index]));
}

function pathsContain(paths: ContentPath[], path: ContentPath): boolean {
  return paths.some((candidate) => pathsEqual(candidate, path));
}

function moveItem(items: unknown[], fromIndex: number, toIndex: number): unknown[] {
  const reorderedItems = [...items];
  const [item] = reorderedItems.splice(fromIndex, 1);
  reorderedItems.splice(toIndex, 0, item);
  return reorderedItems;
}

function newCollectionItem(schema: ContentObjectSchema): ContentValue {
  return { id: newItemId(), ...defaultObjectValue(schema) };
}

function defaultObjectValue(schema: ContentObjectSchema): ContentValue {
  return Object.fromEntries(schema.children.map((child) => [child.key, defaultNodeValue(child)]));
}

function defaultNodeValue(schema: ContentNodeSchema): unknown {
  if (schema.kind === "field") return schema.default ?? "";
  if (schema.kind === "object") return defaultObjectValue(schema);

  return Array.from({ length: schema.min_items }, () => newCollectionItem(schema.item));
}

function newItemId(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") return globalThis.crypto.randomUUID();

  return `item-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
